// ============================================================================
// /tasks/:taskId — deep-dive view for a single task. Shows the full record,
// completion notes, who created/assigned/completed it, and lets the user open
// the edit modal or mark complete / defer / reopen inline.
// ============================================================================

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  CornerUpLeft,
  Loader2,
  Pencil,
  RotateCcw,
  Send,
  UserPlus,
} from 'lucide-react';
import {
  Button,
  ErrorState,
  PageHeader,
  Skeleton,
  useToast,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import type { FacilityTask, TaskStatus } from '../../shared/types';
import { useTaskById, updateTask, addComment, reopenTask, resolveTask, sendTaskBack } from './hooks/use-task-data';
import { useTaskActorNames } from './hooks/use-task-form-options';
import { ResolveModal, ReassignModal, SendBackModal } from './task-action-modals';
import TaskForm from './task-form';
import { NO_BUILDING } from './no-building';

// A task with no building says so (no-building.ts). Before 8 Oct this page drew
// an empty link to a page that does not exist.
export { NO_BUILDING } from './no-building';

function fmt(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d.length === 10 ? d + 'T00:00:00' : d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const STATUS_STYLE: Record<TaskStatus, string> = {
  Open: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  In_Progress: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Done: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  Deferred: 'bg-tcs-surface text-tcs-text-secondary',
};

export default function TaskDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { employee } = useAuth();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useTaskById(taskId ?? null);
  const [editOpen, setEditOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // ⛔ Names on THIS record come from the resolver, never from the assignable
  // list (TCSD-0163). A task filed or commented on by someone who has since
  // left, or by a temp, still has to render their NAME — the assignable list
  // deliberately excludes exactly those people, so building this map from it
  // would print a bare EID- string on a historical record, where nobody is
  // looking. Ask only for the ids actually on this card.
  const actorNames = useTaskActorNames([
    data?.created_by,
    data?.assigned_to,
    data?.completed_by,
    ...(data?.comments ?? []).map((c) => c.author_eid),
  ]);
  const empName = useMemo(() => new Map(Object.entries(actorNames)), [actorNames]);

  // AFB-0047 — creator visibility + send-back. Sub_Auto rows are minted by
  // the sub-onboarding gate (no human creator) → show "System (sub
  // onboarding)" and disable Send back.
  const isSystemTask = data?.source === 'Sub_Auto';
  const creatorLabel = isSystemTask
    ? 'System (sub onboarding)'
    : data?.created_by
      ? empName.get(data.created_by) ?? data.created_by
      : 'Unknown';
  const canSendBack =
    !isSystemTask &&
    !!data?.created_by &&
    data.status !== 'Done' &&
    data.status !== 'Deferred';

  const onSendBack = useCallback(
    async (reason: string) => {
      if (!data || !employee?.employee_id || !data.created_by) return;
      setActionError(null);
      // Reason lands in the comments jsonb thread, then the task goes back
      // to its creator.
      await sendTaskBack(
        { task_id: data.task_id, created_by: data.created_by },
        reason,
        creatorLabel,
        employee.employee_id,
      );
      refetch();
      toast.success(`Sent back to ${creatorLabel}`, {
        description: data.task_id,
      });
    },
    [data, employee, refetch, toast, creatorLabel],
  );

  // Resolve = close with a required note. The note is written to
  // completion_notes AND posted as the final comment so the creator sees it.
  const onResolve = useCallback(
    async (status: 'Done' | 'Deferred', note: string) => {
      if (!data || !employee?.employee_id) return;
      setActionError(null);
      await resolveTask(data.task_id, status, note, employee.employee_id);
      refetch();
      toast.success(`Task ${status === 'Done' ? 'resolved' : 'deferred'}`, {
        description: data.task_id,
      });
    },
    [data, employee, refetch, toast],
  );

  const onReassign = useCallback(
    async (assignedTo: string | null) => {
      if (!data) return;
      setActionError(null);
      await updateTask(data.task_id, { assigned_to: assignedTo });
      refetch();
      toast.success(
        assignedTo
          ? `Reassigned to ${empName.get(assignedTo) ?? assignedTo}`
          : 'Task unassigned',
        { description: data.task_id },
      );
    },
    [data, refetch, toast, empName],
  );

  const onPostComment = useCallback(async () => {
    if (!data || !employee?.employee_id) return;
    const body = commentBody.trim();
    if (!body) return;
    setPostingComment(true);
    setActionError(null);
    try {
      await addComment(data.task_id, body, employee.employee_id);
      setCommentBody('');
      refetch();
    } catch (err) {
      console.error('[tasks] comment failed', err);
      setActionError(
        errorMessage(err, 'Could not post comment.', 'tasks:comment'),
      );
    } finally {
      setPostingComment(false);
    }
  }, [data, employee, commentBody, refetch]);

  const onReopen = useCallback(async () => {
    if (!data) return;
    setActionError(null);
    try {
      await reopenTask(data.task_id);
      refetch();
    } catch (err) {
      console.error('[tasks] reopen failed', err);
      setActionError(errorMessage(err, 'Could not reopen.', 'tasks:reopen'));
    }
  }, [data, refetch]);

  return (
    <div className="mx-auto max-w-3xl p-4 lg:p-6">
      <button
        type="button"
        className="mb-3 inline-flex items-center gap-1 text-xs text-tcs-text-muted hover:text-tcs-text"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={14} /> Back
      </button>
      {isLoading && (
        <div className="space-y-3">
          <Skeleton block height={32} />
          <Skeleton block height={140} />
        </div>
      )}
      {error && (
        <ErrorState
          title="Couldn't load task"
          body={error}
          onRetry={refetch}
        />
      )}
      {!isLoading && !error && !data && (
        <ErrorState
          title="Task not found"
          body={`No task with id ${taskId}.`}
        />
      )}
      {data && (
        <>
          <PageHeader
            eyebrow={data.task_id}
            title={data.description}
            meta={
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_STYLE[data.status]}`}
              >
                {data.status.replace('_', ' ')}
              </span>
            }
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {data.status !== 'Done' && data.status !== 'Deferred' && (
                  <>
                    {canSendBack && (
                      <span title={`Return this task to ${creatorLabel} with a reason`}>
                        <Button variant="ghost" onClick={() => setSendBackOpen(true)}>
                          <CornerUpLeft size={14} className="mr-1" /> Send back
                        </Button>
                      </span>
                    )}
                    <Button variant="ghost" onClick={() => setReassignOpen(true)}>
                      <UserPlus size={14} className="mr-1" /> Reassign
                    </Button>
                    <Button onClick={() => setResolveOpen(true)}>
                      <Check size={14} className="mr-1" /> Resolve
                    </Button>
                  </>
                )}
                {(data.status === 'Done' || data.status === 'Deferred') && (
                  <Button variant="ghost" onClick={onReopen}>
                    <RotateCcw size={14} className="mr-1" /> Reopen
                  </Button>
                )}
                <Button variant="secondary" onClick={() => setEditOpen(true)}>
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
              </div>
            }
          />
          {actionError && (
            <div className="mt-3 rounded-md bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
              {actionError}
            </div>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Detail label="Priority" value={data.priority} />
            <Detail
              label="Facility"
              value={
                data.facility_id ? (
                  <Link
                    to={`/account-management/facilities/${data.facility_id}`}
                    className="hover:underline"
                  >
                    {data.facility_name ?? data.facility_id}
                  </Link>
                ) : (
                  NO_BUILDING
                )
              }
            />
            <Detail label="Due date" value={fmt(data.due_date)} />
            <Detail
              label="Assigned to"
              value={
                [data.assignee_first, data.assignee_last]
                  .filter(Boolean)
                  .join(' ') || 'Unassigned'
              }
            />
            <Detail label="Source" value={data.source.replace('_', ' ')} />
            {/* AFB-0047 — who filed this task, next to when. */}
            <Detail
              label="Created by"
              value={`${creatorLabel} · ${fmt(data.created_date)}`}
            />
            {data.completed_date && (
              <Detail label="Completed" value={fmt(data.completed_date)} />
            )}
          </div>
          {data.completion_notes && (
            <div className="mt-4 rounded-md border border-tcs-border bg-tcs-bg-muted p-3 text-sm text-tcs-text-secondary">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
                Notes
              </div>
              {data.completion_notes}
            </div>
          )}
          {/* Comment thread — the "provide feedback" channel (AFB-0012). */}
          <div className="mt-6">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
              Comments
            </div>
            {(data.comments ?? []).length === 0 ? (
              <p className="text-sm text-tcs-text-muted">
                No comments yet. Add a note or question below.
              </p>
            ) : (
              <ul className="space-y-2">
                {(data.comments ?? []).map((c) => (
                  <li
                    key={c.comment_id}
                    className="rounded-md border border-tcs-border bg-tcs-raised p-3"
                  >
                    <div className="mb-1 flex items-center justify-between text-xs text-tcs-text-muted">
                      <span className="font-medium text-tcs-text-secondary">
                        {empName.get(c.author_eid) ?? c.author_eid}
                      </span>
                      <span>{fmtTime(c.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-tcs-text">
                      {c.body}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex items-start gap-2">
              <textarea
                rows={2}
                className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
                placeholder="Add a comment for the task creator or assignee…"
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
              />
              <Button
                onClick={onPostComment}
                disabled={postingComment || commentBody.trim().length === 0}
              >
                {postingComment ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
              </Button>
            </div>
          </div>

          <ResolveModal
            open={resolveOpen}
            taskId={data.task_id}
            taskDescription={data.description}
            onClose={() => setResolveOpen(false)}
            onSubmit={onResolve}
          />
          <SendBackModal
            open={sendBackOpen}
            taskId={data.task_id}
            creatorLabel={creatorLabel}
            onClose={() => setSendBackOpen(false)}
            onSubmit={onSendBack}
          />
          <ReassignModal
            open={reassignOpen}
            taskId={data.task_id}
            currentAssignee={data.assigned_to}
            currentEid={employee?.employee_id ?? null}
            onClose={() => setReassignOpen(false)}
            onSubmit={onReassign}
          />
          <TaskForm
            open={editOpen}
            initial={data as FacilityTask}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              refetch();
            }}
          />
        </>
      )}
    </div>
  );
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
      <div className="mt-1 text-sm text-tcs-text">{value}</div>
    </div>
  );
}
